import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Search, DollarSign, History,
  X, AlertTriangle, Info, Undo2, Check, Trash2,
} from "lucide-react";
import {
  dollarsInputToCents,
  centsToDisplayDollars,
} from "./adminPricingMoney";

/**
 * P2B.1 · Admin Pricing Manager — inline batch editing.
 *
 * The owner clicks a price cell, types a new value, moves on, edits
 * more prices, then clicks ONE "Save X Changes" button. Nothing hits
 * MongoDB until the batch is reviewed + confirmed with a shared
 * reason.
 *
 * Backend endpoints consumed:
 *   GET   /api/admin/pricing/variants
 *   GET   /api/admin/pricing/stickers
 *   GET   /api/admin/pricing/stickers/{album}/{variant}/{code}/history
 *   PATCH /api/admin/pricing/stickers/batch     ← the ONLY write path
 *
 * The old per-row PATCH endpoint (`/stickers/{album}/{variant}/{code}`)
 * remains as a backend primitive but is no longer invoked from this
 * UI — the individual Edit modal was removed in P2B.1.
 */

const ALBUMS = [
  { slug: "panini-wc-2026", label: "WC 2026",
    variants: ["white", "blue", "gold_crumple", "red", "orange", "purple"] },
  { slug: "panini-wc-russia-2018", label: "RUSSIA 2018",
    variants: ["black_back"] },
];
const ALBUM = "panini-wc-2026";
const VARIANT_ORDER = ["white", "blue", "gold_crumple", "red", "orange", "purple"];
const VARIANT_LABELS = {
  white: "White",
  blue: "Blue",
  gold_crumple: "Gold Crumple",
  red: "Red",
  orange: "Orange",
  purple: "Purple",
  black_back: "Black Back",
};
const PRICE_BUCKETS = ["0.40", "1.00", "2.00", "3.00", "5.00", "6.00",
                        "15.00", "20.00", "other"];
const SORTS = [
  ["album_order", "Album order"],
  ["code", "Sticker code"],
  ["price_asc", "Price · low → high"],
  ["price_desc", "Price · high → low"],
  ["updated_desc", "Recently updated"],
];

const _pendingKey = (r, tier = "regular") =>
  `${r.album_slug}:${r.variant}:${r.code}:${tier}`;

const TIER_REGULAR = "regular";
const TIER_SMALL = "small_order";
const TIER_LABELS = {
  regular: "35+ Price",
  small_order: "Under 35 Price",
};

export default function AdminPricing() {
  const [variantsInfo, setVariantsInfo] = useState(null);
  const [album, setAlbum] = useState(ALBUM);
  const [variant, setVariant] = useState("white");
  const [search, setSearch] = useState("");
  const [priceBucket, setPriceBucket] = useState("");
  const [sort, setSort] = useState("album_order");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);

  // pending edits keyed by "album:variant:code" — survives search /
  // filter / sort / tab changes.
  const [pending, setPending] = useState({});
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [showTabWarning, setShowTabWarning] = useState(null); // {targetVariant}

  const pendingCount = Object.keys(pending).length;
  const hasPending = pendingCount > 0;

  // ── Data loading ───────────────────────────────────────────────
  const loadVariants = useCallback(async () => {
    try {
      const r = await api.get(`/admin/pricing/variants?album_slug=${album}`);
      setVariantsInfo(r.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) ||
              "Failed to load variant list.");
    }
  }, [album]);

  const loadStickers = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        album_slug: album, variant, sort,
      });
      if (search.trim()) params.set("search", search.trim());
      if (priceBucket) params.set("price_bucket", priceBucket);
      const r = await api.get(`/admin/pricing/stickers?${params.toString()}`);
      setRows(Array.isArray(r.data?.rows) ? r.data.rows : []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) ||
              "Failed to load master prices.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [album, variant, search, priceBucket, sort]);

  useEffect(() => { loadVariants(); }, [loadVariants]);
  useEffect(() => { loadStickers(); }, [loadStickers]);

  // Album switch — snap `variant` to the first variant of the new album
  // so we never render a stale (now-mismatched) variant tab.
  useEffect(() => {
    const a = ALBUMS.find((x) => x.slug === album);
    if (a && !a.variants.includes(variant)) setVariant(a.variants[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album]);
  const activeAlbum = ALBUMS.find((x) => x.slug === album) || ALBUMS[0];
  const activeVariantOrder = activeAlbum.variants;

  // ── Unsaved-changes guards ─────────────────────────────────────
  // beforeunload covers tab close / reload / hard navigation.
  useEffect(() => {
    if (!hasPending) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPending]);
  // In-app navigation guard is implemented per-link (see Back
  // button below) because this app uses <BrowserRouter>, which
  // predates the data-router APIs required by useBlocker.
  const confirmLeaveIfPending = useCallback((e) => {
    if (!hasPending) return true;
    const ok = window.confirm(
      `You have ${pendingCount} unsaved price change${pendingCount === 1 ? "" : "s"}. Leave without saving?`,
    );
    if (!ok && e) e.preventDefault();
    return ok;
  }, [hasPending, pendingCount]);

  const activeVariantInfo = useMemo(() => {
    if (!variantsInfo) return null;
    return (variantsInfo.variants || []).find((v) => v.variant === variant);
  }, [variantsInfo, variant]);
  const populatedVariant = activeVariantInfo?.populated;

  // ── Pending edit helpers ────────────────────────────────────────
  // Blue Parallel · when the admin edits the 35+ master price on a
  // Blue row, the small-order tier is derived automatically as
  // max(master, $2). We stage BOTH the regular tier and (if changed)
  // a corresponding small_order tier edit inside the same batch so
  // the two DB values stay locked to the rule.
  const BLUE_SMALL_FLOOR_CENTS = 200;
  const stagePendingEdit = useCallback((row, newCents, tier = TIER_REGULAR) => {
    setPending((prev) => {
      const next = { ...prev };
      const stageOne = (t, newValue, originalValue) => {
        const key = _pendingKey(row, t);
        if (newValue === originalValue) {
          delete next[key];
        } else {
          next[key] = {
            key,
            album_slug: row.album_slug,
            variant: row.variant,
            code: row.code,
            tier: t,
            display_name: row.display_name,
            country_code: row.country_code,
            original_price_cents: originalValue,
            new_price_cents: newValue,
            expected_updated_at: row.updated_at || null,
          };
        }
      };
      if (tier === TIER_REGULAR) {
        stageOne(TIER_REGULAR, newCents, row.price_cents);
      } else {
        stageOne(TIER_SMALL, newCents, row.small_order_price_cents);
      }
      // For Blue: keep the small-order tier synced to max(master, $2)
      // whenever the master (regular) tier is being edited. Admin
      // never has to touch the under-35 column for Blue.
      if (row.variant === "blue" && tier === TIER_REGULAR) {
        const derivedSmall = Math.max(newCents, BLUE_SMALL_FLOOR_CENTS);
        stageOne(TIER_SMALL, derivedSmall, row.small_order_price_cents);
      }
      return next;
    });
  }, []);

  const undoOne = useCallback((row, tier = TIER_REGULAR) => {
    setPending((prev) => {
      const next = { ...prev };
      delete next[_pendingKey(row, tier)];
      return next;
    });
  }, []);

  const discardAll = useCallback(() => {
    if (!window.confirm(`Discard all ${pendingCount} unsaved changes?`)) return;
    setPending({});
  }, [pendingCount]);

  // ── Tab switching with unsaved edits ────────────────────────────
  const switchVariant = useCallback((nextVariant) => {
    if (nextVariant === variant) return;
    if (hasPending) {
      setShowTabWarning({ target: nextVariant });
      return;
    }
    setVariant(nextVariant);
    setSearch("");
    setPriceBucket("");
  }, [variant, hasPending]);

  // ── Save batch ──────────────────────────────────────────────────
  const runBatchSave = useCallback(async (reason) => {
    setSaving(true);
    try {
      const changes = Object.values(pending).map((p) => ({
        album_slug: p.album_slug,
        variant: p.variant,
        code: p.code,
        tier: p.tier || TIER_REGULAR,
        new_price_cents: p.new_price_cents,
        expected_updated_at: p.expected_updated_at,
      }));
      const r = await api.patch("/admin/pricing/stickers/batch", {
        reason, changes,
      });
      // Success: patch local rows optimistically, clear pending.
      setRows((prev) => {
        const byIdent = new Map();
        for (const ap of r.data?.applied || []) {
          const rowKey = `${ap.album_slug}:${ap.variant}:${ap.code}`;
          byIdent.set(rowKey, ap);
        }
        return prev.map((row) => {
          const rowKey = `${row.album_slug}:${row.variant}:${row.code}`;
          const ap = byIdent.get(rowKey);
          if (!ap) return row;
          return {
            ...row,
            price_cents: ap.current_price_cents != null
              ? ap.current_price_cents
              : row.price_cents,
            price_dollars: ap.current_price_cents != null
              ? ap.current_price_cents / 100
              : row.price_dollars,
            small_order_price_cents: ap.current_small_order_price_cents !== undefined
              ? ap.current_small_order_price_cents
              : row.small_order_price_cents,
            small_order_price_dollars:
              ap.current_small_order_price_cents != null
                ? ap.current_small_order_price_cents / 100
                : row.small_order_price_dollars,
            updated_at: ap.updated_at,
            updated_by_admin_id: r.data?.changed_by,
            source: "batch_admin_write",
          };
        });
      });
      setPending({});
      setReviewing(false);
      toast.success(`Saved ${r.data?.total_applied} master price ${r.data?.total_applied === 1 ? "change" : "changes"}`);
      loadVariants();
      return { ok: true };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      return { ok: false, status: e?.response?.status, detail };
    } finally {
      setSaving(false);
    }
  }, [pending, loadVariants]);

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div
      className={`p-4 md:p-6 space-y-5 max-w-7xl mx-auto ${hasPending ? "pb-32" : ""}`}
      data-testid="admin-pricing"
    >
      <SEO title="Pricing Manager · Admin · CollectorUp" />

      <div>
        <Link
          to="/admin"
          onClick={confirmLeaveIfPending}
          data-testid="pricing-back-to-admin"
          className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] shadow-[3px_3px_0_var(--ink)] transition"
        >
          <ArrowLeft size={14} /> Back to Admin Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
            Master Price
          </div>
          <h1 className="font-display text-3xl" data-testid="pricing-title">
            Pricing Manager
          </h1>
        </div>
        <button
          type="button"
          onClick={() => { loadVariants(); loadStickers(); }}
          className="rounded-full border-2 border-[var(--ink)] px-3 py-1.5 text-sm flex items-center gap-1 bg-white hover:bg-neutral-50 disabled:opacity-50"
          data-testid="pricing-refresh"
          disabled={loading}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Master-price explanation banner (P2B.2 concise copy) */}
      <div
        className="rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] p-3 flex items-start gap-2 shadow-[3px_3px_0_var(--ink)]"
        data-testid="pricing-master-message"
      >
        <Info size={18} className="mt-0.5 text-[var(--panini-red)] shrink-0" />
        <div className="text-sm">
          Master price changes affect future quotes only. Existing frozen
          quotes, orders, invoices, and fulfillment records will not change.
        </div>
      </div>

      {/* Album switcher — adds Russia 2018 alongside WC 2026 */}
      <div
        className="flex gap-2 flex-wrap items-center"
        data-testid="pricing-album-switcher"
      >
        <span className="text-[11px] uppercase tracking-widest font-bold text-[var(--smoke)]">
          Album
        </span>
        {ALBUMS.map((a) => {
          const active = a.slug === album;
          return (
            <button
              key={a.slug}
              type="button"
              onClick={() => setAlbum(a.slug)}
              data-testid={`pricing-album-${a.slug}`}
              className={`px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border-2 border-[var(--ink)] transition ${
                active
                  ? "bg-[var(--ink)] text-white shadow-[3px_3px_0_var(--ink)]"
                  : "bg-white hover:bg-neutral-50"
              }`}
            >
              {a.label}
            </button>
          );
        })}
      </div>

      {/* Variant tabs */}
      <div
        className="flex gap-2 flex-wrap border-b-2 border-[var(--ink)] pb-2"
        role="tablist"
        data-testid="pricing-variant-tabs"
      >
        {activeVariantOrder.map((v) => {
          const info = (variantsInfo?.variants || []).find((x) => x.variant === v);
          const populated = info?.populated;
          const active = v === variant;
          return (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`pricing-tab-${v}`}
              onClick={() => switchVariant(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border-2 border-[var(--ink)] transition ${
                active
                  ? "bg-[var(--panini-red)] text-white shadow-[3px_3px_0_var(--ink)]"
                  : populated
                    ? "bg-white hover:bg-neutral-50"
                    : "bg-neutral-100 text-neutral-500"
              }`}
            >
              {VARIANT_LABELS[v]}
              <span className="ml-2 text-[10px] opacity-80">
                {info ? info.count : "…"}
              </span>
            </button>
          );
        })}
      </div>

      {/* Empty state */}
      {!populatedVariant && variantsInfo ? (
        <div
          className="rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-8 text-center"
          data-testid={`pricing-empty-${variant}`}
        >
          <Info className="mx-auto mb-2 text-[var(--smoke)]" size={28} />
          <h2 className="font-display text-xl mb-1">
            No {VARIANT_LABELS[variant]} master prices configured yet
          </h2>
          <p className="text-sm text-[var(--smoke)] max-w-md mx-auto">
            The {VARIANT_LABELS[variant]} variant is schema-legal but not
            activated for Panini WC 2026.
          </p>
        </div>
      ) : (
        <>
          {/* Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <div className="relative md:col-span-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
              <input
                type="text"
                placeholder="Search code, player, country (ARG10, Messi, Argentina)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="pricing-search-input"
                className="w-full pl-8 pr-3 py-1.5 border-2 border-[var(--ink)] rounded-full text-sm"
              />
            </div>
            <select
              value={priceBucket}
              onChange={(e) => setPriceBucket(e.target.value)}
              data-testid="pricing-filter-price"
              className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-sm bg-white"
            >
              <option value="">All prices</option>
              {PRICE_BUCKETS.map((b) => (
                <option key={b} value={b}>
                  {b === "other" ? "Other" : `$${b}`}
                </option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="pricing-sort"
              className="border-2 border-[var(--ink)] rounded-full px-3 py-1.5 text-sm bg-white"
            >
              {SORTS.map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>

          {err ? (
            <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-3 text-sm text-[var(--panini-red)] flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5" />
              <span>{err}</span>
            </div>
          ) : null}

          <div className="text-xs text-[var(--smoke)]" data-testid="pricing-result-count">
            {loading
              ? "Loading…"
              : `${rows.length} ${rows.length === 1 ? "record" : "records"}`}
            {hasPending ? (
              <span className="ml-2 text-[var(--panini-red)] font-bold">
                · {pendingCount} unsaved
              </span>
            ) : null}
          </div>

          {/* Desktop table + mobile cards */}
          <div className="rounded-2xl border-2 border-[var(--ink)] bg-white shadow-[3px_3px_0_var(--ink)] overflow-hidden">
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--panini-yellow-soft)] border-b-2 border-[var(--ink)]">
                  <tr>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-24">Code</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[11px] tracking-widest">Subject</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-24">Country</th>
                    <th className="text-right px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-40" data-testid="pricing-col-35-plus">35+ Price</th>
                    <th className="text-right px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-40" data-testid="pricing-col-under-35">Under 35 Price</th>
                    <th className="text-left px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-40">Updated</th>
                    <th className="text-right px-3 py-2 font-bold uppercase text-[11px] tracking-widest w-28">History</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const pReg = pending[_pendingKey(r, TIER_REGULAR)];
                    const pSmall = pending[_pendingKey(r, TIER_SMALL)];
                    return (
                      <PriceRowDesktop
                        key={_pendingKey(r, "row")}
                        row={r}
                        pendingRegular={pReg}
                        pendingSmall={pSmall}
                        onStage={stagePendingEdit}
                        onUndo={undoOne}
                        onHistory={setHistoryFor}
                      />
                    );
                  })}
                  {!loading && rows.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-[var(--smoke)]">No stickers match your filters.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y-2 divide-neutral-200">
              {rows.map((r) => {
                const pReg = pending[_pendingKey(r, TIER_REGULAR)];
                const pSmall = pending[_pendingKey(r, TIER_SMALL)];
                return (
                  <PriceRowMobile
                    key={_pendingKey(r, "m")}
                    row={r}
                    pendingRegular={pReg}
                    pendingSmall={pSmall}
                    onStage={stagePendingEdit}
                    onUndo={undoOne}
                    onHistory={setHistoryFor}
                  />
                );
              })}
              {!loading && rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-[var(--smoke)]">No stickers match your filters.</div>
              ) : null}
            </div>
          </div>
        </>
      )}

      {/* Sticky save bar */}
      {hasPending ? (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-[var(--ink)] shadow-[0_-4px_0_var(--ink)]"
          data-testid="pricing-save-bar"
        >
          <div className="max-w-7xl mx-auto p-3 flex items-center justify-between gap-3">
            <div className="text-sm font-bold" data-testid="pricing-pending-count">
              {pendingCount} unsaved price change{pendingCount === 1 ? "" : "s"}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={discardAll}
                className="rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1"
                data-testid="pricing-discard-all"
              >
                <Trash2 size={12} /> Discard All
              </button>
              <button
                type="button"
                onClick={() => setReviewing(true)}
                disabled={saving}
                className="rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-4 py-1.5 text-xs font-bold uppercase tracking-widest shadow-[3px_3px_0_var(--ink)] hover:opacity-90 flex items-center gap-1 disabled:opacity-40"
                data-testid="pricing-save-btn"
              >
                <Check size={12} /> Save {pendingCount} Change{pendingCount === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reviewing ? (
        <BatchReviewModal
          pending={pending}
          onClose={() => setReviewing(false)}
          onSave={runBatchSave}
          saving={saving}
        />
      ) : null}

      {historyFor ? (
        <HistoryDrawer
          row={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      ) : null}

      {showTabWarning ? (
        <TabSwitchWarning
          targetVariant={showTabWarning.target}
          pendingCount={pendingCount}
          onCancel={() => setShowTabWarning(null)}
          onKeep={() => {
            setVariant(showTabWarning.target);
            setSearch(""); setPriceBucket("");
            setShowTabWarning(null);
          }}
          onDiscard={() => {
            setPending({});
            setVariant(showTabWarning.target);
            setSearch(""); setPriceBucket("");
            setShowTabWarning(null);
          }}
        />
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Inline price cell (desktop row)                                     */
/* ────────────────────────────────────────────────────────────────── */
function PriceRowDesktop({ row, pendingRegular, pendingSmall, onStage, onUndo, onHistory }) {
  const anyPending = !!(pendingRegular || pendingSmall);
  return (
    <tr
      className={`border-b border-neutral-200 ${
        anyPending ? "bg-[var(--panini-yellow-soft)]/60" : "hover:bg-neutral-50"
      }`}
      data-testid={`pricing-row-${row.code}`}
    >
      <td className="px-3 py-2 font-mono font-bold">{row.code}</td>
      <td className="px-3 py-2">{row.display_name || <span className="text-[var(--smoke)]">—</span>}</td>
      <td className="px-3 py-2 text-[var(--smoke)]">{row.country_code || "—"}</td>
      <td className="px-3 py-2 text-right" data-testid={`pricing-price-cell-${row.code}`}>
        <InlinePriceEditor
          row={row}
          tier={TIER_REGULAR}
          pending={pendingRegular}
          onStage={onStage}
          onUndo={onUndo}
        />
      </td>
      <td className="px-3 py-2 text-right" data-testid={`pricing-small-cell-${row.code}`}>
        <InlinePriceEditor
          row={row}
          tier={TIER_SMALL}
          pending={pendingSmall}
          onStage={onStage}
          onUndo={onUndo}
          readOnly={row.variant === "blue"}
        />
      </td>
      <td className="px-3 py-2 text-xs text-[var(--smoke)]">
        {row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}
        {row.updated_by_admin_id ? (
          <div className="text-[10px]">by {row.updated_by_admin_id}</div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={() => onHistory(row)}
          data-testid={`pricing-history-btn-${row.code}`}
          className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] bg-white px-2 py-1 text-[11px] font-bold uppercase tracking-widest hover:bg-neutral-50"
        >
          <History size={12} /> History
        </button>
      </td>
    </tr>
  );
}

function PriceRowMobile({ row, pendingRegular, pendingSmall, onStage, onUndo, onHistory }) {
  return (
    <div className="p-3 flex flex-col gap-2" data-testid={`pricing-row-mobile-${row.code}`}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono font-bold text-base">{row.code}</div>
          <div className="text-sm text-[var(--ink)] break-words">
            {row.display_name || <span className="text-[var(--smoke)]">—</span>}
          </div>
          <div className="text-[11px] text-[var(--smoke)]">
            {row.country_code || "—"}
          </div>
        </div>
        <div className="text-right shrink-0 space-y-1">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[var(--smoke)] font-bold">35+</div>
            <InlinePriceEditor
              row={row}
              tier={TIER_REGULAR}
              pending={pendingRegular}
              onStage={onStage}
              onUndo={onUndo}
            />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[var(--smoke)] font-bold">Under 35</div>
            <InlinePriceEditor
              row={row}
              tier={TIER_SMALL}
              pending={pendingSmall}
              onStage={onStage}
              onUndo={onUndo}
              readOnly={row.variant === "blue"}
            />
          </div>
          {row.updated_at ? (
            <div className="text-[10px] text-[var(--smoke)] mt-0.5">
              {new Date(row.updated_at).toLocaleDateString()}
            </div>
          ) : null}
        </div>
      </div>
      <div>
        <button
          type="button"
          onClick={() => onHistory(row)}
          className="inline-flex items-center justify-center gap-1 rounded-full border-2 border-[var(--ink)] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-widest hover:bg-neutral-50"
          data-testid={`pricing-history-btn-mobile-${row.code}`}
        >
          <History size={12} /> History
        </button>
      </div>
    </div>
  );
}

function InlinePriceEditor({ row, tier = TIER_REGULAR, pending, onStage, onUndo, readOnly = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [localErr, setLocalErr] = useState(null);
  const inputRef = useRef(null);

  const originalCents = tier === TIER_REGULAR
    ? row.price_cents
    : (row.small_order_price_cents ?? null);
  const displayCents = pending ? pending.new_price_cents : originalCents;
  const missing = displayCents == null;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function beginEdit() {
    if (readOnly) return;
    setDraft(displayCents != null ? (displayCents / 100).toFixed(2) : "");
    setLocalErr(null);
    setEditing(true);
  }

  function commit() {
    const parsed = dollarsInputToCents(draft);
    if (parsed.cents == null) {
      setLocalErr(parsed.error);
      return;
    }
    setLocalErr(null);
    onStage(row, parsed.cents, tier);
    setEditing(false);
  }

  function cancel() {
    setLocalErr(null);
    setEditing(false);
  }

  if (editing) {
    return (
      <div className="inline-flex flex-col items-end">
        <div className="inline-flex items-center gap-1">
          <span className="text-[var(--smoke)] font-mono">$</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commit(); }
              if (e.key === "Escape") { e.preventDefault(); cancel(); }
            }}
            className="w-24 px-2 py-1 border-2 border-[var(--panini-red)] rounded-md text-right font-mono text-lg font-bold"
            data-testid={`pricing-inline-input-${tier}-${row.code}`}
          />
        </div>
        {localErr ? (
          <div
            className="text-[10px] text-[var(--panini-red)] mt-0.5 max-w-[10rem] text-right"
            data-testid={`pricing-inline-error-${tier}-${row.code}`}
          >
            {localErr}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="inline-flex flex-col items-end">
      <button
        type="button"
        onClick={beginEdit}
        disabled={readOnly}
        data-testid={`pricing-price-${tier}-${row.code}`}
        className={`font-mono font-bold text-lg tracking-tight tabular-nums px-2 py-0.5 rounded-md ${
          readOnly
            ? "cursor-default text-[var(--ink)]/70"
            : "hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-[var(--panini-red)]"
        } ${pending ? "text-[var(--panini-red)]" : missing ? "text-[var(--smoke)]" : "text-[var(--ink)]"}`}
        title={readOnly
          ? "Auto-computed from the 35+ Master Price (Blue rule: max(master, $2))"
          : `Click to edit ${TIER_LABELS[tier]}`}
      >
        {missing ? "—" : centsToDisplayDollars(displayCents)}
      </button>
      {readOnly && !pending && (
        <div className="text-[9px] uppercase tracking-widest text-[var(--smoke)] font-bold">
          auto
        </div>
      )}
      {pending ? (
        <div
          className="text-[10px] text-[var(--smoke)] flex items-center gap-1"
          data-testid={`pricing-was-${tier}-${row.code}`}
        >
          was {originalCents != null ? centsToDisplayDollars(originalCents) : "—"}
          <button
            type="button"
            onClick={() => onUndo(row, tier)}
            className="ml-1 inline-flex items-center gap-0.5 text-[var(--panini-red)] font-bold hover:underline"
            data-testid={`pricing-undo-${tier}-${row.code}`}
          >
            <Undo2 size={10} /> Undo
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Batch review modal                                                  */
/* ────────────────────────────────────────────────────────────────── */
function BatchReviewModal({ pending, onClose, onSave, saving }) {
  const items = Object.values(pending);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);
  const [batchErrors, setBatchErrors] = useState(null); // {kind, errors}

  async function confirm() {
    setErr(null);
    setBatchErrors(null);
    if (!reason.trim()) {
      setErr("Enter a reason for this batch of changes.");
      return;
    }
    const res = await onSave(reason.trim());
    if (!res.ok) {
      if (res.status === 409 && res.detail?.kind === "pricing_write_busy") {
        setErr(res.detail.detail || "Another edit is in flight. Retry in a moment.");
      } else if (res.status === 409 && res.detail?.errors) {
        setBatchErrors({ kind: "validation", errors: res.detail.errors });
      } else {
        setErr(formatApiError(res.detail) || "Failed to save changes.");
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="pricing-batch-review"
    >
      <div className="bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[6px_6px_0_var(--ink)] max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
              Master Price · Future quotes only
            </div>
            <div className="font-display text-xl">
              Review {items.length} price change{items.length === 1 ? "" : "s"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pricing-review-close"
            className="rounded-full border-2 border-[var(--ink)] p-1 hover:bg-neutral-100"
            aria-label="Close"
            disabled={saving}
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-3">
          <div className="rounded-xl border-2 border-dashed border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-2 text-xs text-[var(--ink)]">
            These changes affect <strong>FUTURE</strong> pricing calculations
            only. Existing frozen orders, invoices and quote snapshots are
            not changed.
          </div>

          <ul className="divide-y divide-neutral-200 border-2 border-[var(--ink)] rounded-xl overflow-hidden">
            {items.map((p) => (
              <li key={p.key} className="p-2 flex items-baseline justify-between gap-2" data-testid={`pricing-review-item-${p.code}-${p.tier || "regular"}`}>
                <div className="min-w-0">
                  <div className="font-mono font-bold text-sm">{p.code}</div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
                    {TIER_LABELS[p.tier || "regular"]}
                  </div>
                  {p.display_name ? (
                    <div className="text-xs text-[var(--smoke)] truncate">{p.display_name}{p.country_code ? ` · ${p.country_code}` : ""}</div>
                  ) : null}
                </div>
                <div className="font-mono text-sm text-right shrink-0">
                  <span className="text-[var(--smoke)] line-through">
                    {p.original_price_cents != null ? centsToDisplayDollars(p.original_price_cents) : "—"}
                  </span>
                  {" → "}
                  <strong className="text-[var(--panini-red)]">{centsToDisplayDollars(p.new_price_cents)}</strong>
                </div>
              </li>
            ))}
          </ul>

          <div>
            <label htmlFor="pricing-batch-reason" className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold block mb-1">
              Reason for this batch (required, shared across all changes)
            </label>
            <textarea
              id="pricing-batch-reason"
              data-testid="pricing-batch-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Market adjustment · Inventory scarcity · Pricing correction · …"
              className="w-full border-2 border-[var(--ink)] rounded-xl px-3 py-2 text-sm"
              maxLength={500}
              disabled={saving}
            />
          </div>

          {err ? (
            <div className="rounded-xl border-2 border-[var(--panini-red)] bg-red-50 p-2 text-xs text-[var(--panini-red)]" data-testid="pricing-batch-error">
              {err}
            </div>
          ) : null}

          {batchErrors ? (
            <div className="rounded-xl border-2 border-[var(--panini-red)] bg-red-50 p-3 text-xs text-[var(--panini-red)] space-y-1" data-testid="pricing-batch-conflicts">
              <div className="font-bold uppercase tracking-widest text-[11px]">
                No changes saved — resolve the conflicts below and try again.
              </div>
              <ul className="list-disc pl-4">
                {batchErrors.errors.map((e, i) => (
                  <li key={i}>
                    <span className="font-mono font-bold">{e.code}</span>
                    {" — "}{e.detail || e.kind}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="p-4 border-t-2 border-[var(--ink)] flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 rounded-full border-2 border-[var(--ink)] px-3 py-2 text-sm font-bold uppercase tracking-widest bg-white hover:bg-neutral-50 disabled:opacity-40"
            data-testid="pricing-review-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={saving || !reason.trim()}
            className="flex-1 rounded-full border-2 border-[var(--ink)] px-3 py-2 text-sm font-bold uppercase tracking-widest bg-[var(--panini-red)] text-white shadow-[3px_3px_0_var(--ink)] hover:opacity-90 disabled:opacity-40"
            data-testid="pricing-review-confirm"
          >
            {saving ? "Saving…" : `Confirm & Save ${items.length} Change${items.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Tab-switch confirmation                                             */
/* ────────────────────────────────────────────────────────────────── */
function TabSwitchWarning({ targetVariant, pendingCount, onCancel, onKeep, onDiscard }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white border-2 border-[var(--ink)] rounded-2xl shadow-[6px_6px_0_var(--ink)] max-w-md w-full p-4 space-y-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">Unsaved changes</div>
          <div className="font-display text-lg">
            Switch to {VARIANT_LABELS[targetVariant]}?
          </div>
        </div>
        <p className="text-sm">
          You have <strong>{pendingCount}</strong> unsaved price change{pendingCount === 1 ? "" : "s"}. Keep them for later or discard?
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onKeep}
            className="rounded-full border-2 border-[var(--ink)] bg-white px-3 py-2 text-sm font-bold uppercase tracking-widest hover:bg-neutral-50"
          >
            Keep edits · switch tab
          </button>
          <button
            type="button"
            onClick={onDiscard}
            className="rounded-full border-2 border-[var(--panini-red)] bg-white text-[var(--panini-red)] px-3 py-2 text-sm font-bold uppercase tracking-widest hover:bg-red-50"
          >
            Discard edits · switch tab
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border-2 border-[var(--ink)] px-3 py-2 text-sm font-bold uppercase tracking-widest bg-neutral-50 hover:bg-neutral-100"
          >
            Stay on this tab
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* History drawer                                                      */
/* ────────────────────────────────────────────────────────────────── */
function HistoryDrawer({ row, onClose }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = `/admin/pricing/stickers/${encodeURIComponent(row.album_slug)}/${encodeURIComponent(row.variant)}/${encodeURIComponent(row.code)}/history`;
        const r = await api.get(url);
        if (!cancelled) setItems(r.data?.history || []);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e?.response?.data?.detail) || "Failed to load history.");
      }
    })();
    return () => { cancelled = true; };
  }, [row]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="pricing-history-drawer"
    >
      <div className="bg-white border-l-2 border-[var(--ink)] shadow-[-6px_0_0_var(--ink)] max-w-md w-full h-full overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b-2 border-[var(--ink)] sticky top-0 bg-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">Price history</div>
            <div className="font-display text-xl">{row.code}</div>
            {row.display_name ? (
              <div className="text-xs text-[var(--smoke)]">{row.display_name}{row.country_code ? ` · ${row.country_code}` : ""}</div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="pricing-history-close"
            className="rounded-full border-2 border-[var(--ink)] p-1 hover:bg-neutral-100"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {err ? (
            <div className="rounded-xl border-2 border-[var(--panini-red)] bg-red-50 p-3 text-sm text-[var(--panini-red)]">{err}</div>
          ) : items == null ? (
            <div className="text-sm text-[var(--smoke)]">Loading history…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[var(--smoke)]" data-testid="pricing-history-empty">No history yet.</div>
          ) : (
            items.map((h, i) => (
              <div key={i} className="rounded-xl border-2 border-[var(--ink)] p-3 space-y-1 shadow-[2px_2px_0_var(--ink)]" data-testid={`pricing-history-item-${i}`}>
                <div className="text-[11px] uppercase tracking-widest text-[var(--smoke)] font-bold flex items-center justify-between gap-2">
                  <span>{h.changed_at ? new Date(h.changed_at).toLocaleString() : "—"}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full border border-[var(--ink)] bg-[var(--panini-yellow-soft)] text-[var(--ink)]" data-testid={`pricing-history-tier-${i}`}>
                    {h.tier === "small_order" ? "Under 35" : "35+"}
                  </span>
                </div>
                <div className="font-mono font-bold text-base">
                  {h.old_price_cents != null ? centsToDisplayDollars(h.old_price_cents) : "—"} → {centsToDisplayDollars(h.new_price_cents)}
                </div>
                <div className="text-sm">
                  {h.reason || <span className="text-[var(--smoke)] italic">no reason recorded</span>}
                </div>
                <div className="text-[11px] text-[var(--smoke)]">
                  by {h.changed_by || "unknown"}
                  {h.source ? ` · ${h.source}` : ""}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
